import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  TextField,
  InputLabel,
  FormControl,
  Select,
  MenuItem,
  Button,
  styled,
  LinearProgress,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import moment from 'moment';

import Services from '../../../services';
import {
  IEmployeeRegistrationPayload,
  IEmployeeSearchCodeResponse,
} from '../../../interfaces/employee.interface';
import { IRegistration } from '../../../interfaces/registration.interface';

const styles = {
  title: (theme: any) => ({
    color: theme.palette.primary.main,
    fontSize: '1.75rem',
  }),

  hideFormContainer: {
    width: '100%',
    flexGrow: 1,
    mt: '1rem',
    fontSize: '1.25rem',
    ml: '1rem',
  },

  formContainer: {
    flexGrow: 1,
    mt: '2rem',
    fontSize: '1.25rem',
  },
};

const Header = styled(Typography)(({ theme }) => ({
  color: theme.palette.primary.main,
  fontSize: '1.75rem',
}));

const AutoFilledData = styled(TextField)(({ theme }) => ({
  input: { color: theme.palette.primary.main },
}));

const initialState: IEmployeeRegistrationPayload = {
  /* eslint-disable camelcase */
  lastname: '',
  firstname: '',
  middlename: '',
  birthdate: '',
  department: '',
  mobile_no: '',
  email_address: '',
  company: '',
  code: '',
  clearance_application_date: '' + moment().format('YYYY-MM-DD'),
  last_working_day: '',
  reason: '',
  dissatisfaction_cause: '',
  end_contract_cause: '',
  remarks: '',
};

const EmployeeRegistrationForm = ({ module }: IRegistration) => {
  const navigate = useNavigate();

  const [showHeader, setShowHeader] = useState<boolean>(true);
  const [fetchInfo, setFetchInfo] = useState<boolean>(false);
  const [isSubmit, setIsSubmit] = useState<boolean>(false);
  const [errorHandler, setErrorHandler] = useState<any>();
  const [employeeInfo, setEmployeeInfo] = useState<IEmployeeSearchCodeResponse>({
    data: {
      /* eslint-disable camelcase */
      base_of_assignment: '',
      code: '',
      company: '',
      department: '',
      hiring_date: '',
      position: '',
      province: '',
    },
    success: false,
    errors: [],
  });
  const [companyList, setCompanyList] = useState<Array<string>>([]);
  const [reasonList, setReasonList] = useState<Array<string>>([]);
  const [dissatisfactionCauseList, setDissatisfactionCauseList] = useState<Array<string>>([]);
  const [endContractCauseList, setEndContractCauseList] = useState<Array<string>>([]);
  const [formvalue, setFormvalue] = useState<IEmployeeRegistrationPayload>(initialState);

  const onChangeFormvalueHandler = (e: string, identifier: string) => {
    setFormvalue((prev) => ({ ...prev, [identifier]: e }));

    if (errorHandler) {
      setErrorHandler((prev: any) => {
        const newErrorData = { ...prev };
        delete newErrorData[identifier];
        return newErrorData;
      });
    }
  };

  useEffect(() => {
    onFetchCompanyList();
    onFetchReasonList();
    onFetcDissatisfactionCauseList();

    if (module) setShowHeader(false);
  }, []);

  const onFetchCompanyList = async () => {
    const {
      data: { items },
    } = await Services.api.common.fetchCompany();

    setCompanyList(items);
  };

  const onFetchReasonList = async () => {
    const {
      data: { items },
    } = await Services.api.common.fetchReason();

    setReasonList(items);
  };

  const onFetcDissatisfactionCauseList = async () => {
    const {
      data: { dissatisfaction_cause, end_contract_cause },
    } = await Services.api.common.fetchCause();

    setDissatisfactionCauseList(dissatisfaction_cause);
    setEndContractCauseList(end_contract_cause);
  };

  useEffect(() => {
    if (formvalue.code.length > 3 && formvalue.company) {
      setFetchInfo(true);
    }

    const timer = setTimeout(() => {
      onCheckEmployee();
      setFetchInfo(false);
    }, 500);

    return () => clearTimeout(timer);
  }, [formvalue.code, formvalue.company]);

  const onCheckEmployee = async () => {
    const { code, company } = formvalue;
    const payload = { code, company };

    if (code.length > 3 && company) {
      const { data, errors, success } = await Services.api.employee.employment(payload);

      if (success) {
        setEmployeeInfo((prev: any) => ({
          ...prev,
          data,
        }));
      } else {
        const [{ message }] = errors;

        Services.toast('error', message);
        setEmployeeInfo((prev: any) => ({
          ...prev,
          data: {
            base_of_assignment: '',
            code: '',
            company: '',
            department: '',
            hiring_date: '',
            position: '',
            province: '',
          },
        }));
      }
    }
  };

  useEffect(() => {
    if (employeeInfo) onChangeFormvalueHandler(employeeInfo.data.department, 'department');
  }, [employeeInfo]);

  const onSubmitFormHandler = async () => {
    setIsSubmit(true);
    if (module === 'hr') {
      const { errors, success } = await Services.api.employee.bypass(formvalue);

      if (success) {
        Services.toast('success', 'Application successfully added.');
        navigate('/hr');
      } else {
        const [{ message }] = errors;

        onFormHandlerError(errors);
        Services.toast('error', message);
      }
    } else {
      const { data, errors, success } = await Services.api.employee.create(formvalue);

      if (success) {
        navigate(`/employee/${data}/exit-interview`);
      } else {
        const [{ message }] = errors;

        onFormHandlerError(errors);
        Services.toast('error', message);
      }
    }
  };

  const onFormHandlerError = (errors: any) => {
    errors.forEach((e: any) => {
      const { field, message } = e;

      setErrorHandler((prev: any) => {
        return {
          ...prev,
          [field]: message,
        };
      });
    });
  };

  return (
    <Box sx={{ width: '100%' }}>
      {showHeader && <Header>Online HR Clearance Form</Header>}

      <Box sx={styles.formContainer}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            Personal Details
          </Grid>

          <Grid item sm={4} xs={12}>
            <TextField
              fullWidth
              required
              label='Last Name'
              error={errorHandler?.lastname && isSubmit && !formvalue.lastname}
              helperText={errorHandler?.lastname}
              value={formvalue.lastname}
              onChange={(e) => onChangeFormvalueHandler(e.target.value, 'lastname')}
            />
          </Grid>

          <Grid item sm={4} xs={12}>
            <TextField
              fullWidth
              required
              label='First Name'
              error={errorHandler?.firstname && isSubmit && !formvalue.firstname}
              helperText={errorHandler?.firstname}
              value={formvalue.firstname}
              onChange={(e) => onChangeFormvalueHandler(e.target.value, 'firstname')}
            />
          </Grid>

          <Grid item sm={4} xs={12}>
            <TextField
              fullWidth
              label='Middle Name'
              value={formvalue.middlename}
              onChange={(e) => onChangeFormvalueHandler(e.target.value, 'middlename')}
            />
          </Grid>

          <Grid item sm={6} xs={12}>
            <DatePicker
              disableFuture
              openTo='year'
              views={['year', 'month', 'day']}
              label='Birthdate'
              inputFormat={'YYYY-MM-DD'}
              minDate={moment().subtract(65, 'year')}
              maxDate={moment()}
              value={formvalue.birthdate === '' ? null : formvalue.birthdate}
              onChange={(e) => {
                const date = moment(e).format('YYYY-MM-DD');
                onChangeFormvalueHandler(date, 'birthdate');
              }}
              renderInput={(params) => (
                <TextField
                  required
                  fullWidth
                  {...params}
                  error={errorHandler?.birthdate && isSubmit && !formvalue.birthdate}
                  helperText={errorHandler?.birthdate}
                />
              )}
            />
          </Grid>

          <Grid item sm={6} xs={12}>
            <TextField
              fullWidth
              label='Mobile Number'
              value={formvalue.mobile_no}
              error={errorHandler?.mobile_no && isSubmit && !formvalue.mobile_no}
              helperText={errorHandler?.mobile_no}
              onChange={(e) => onChangeFormvalueHandler(e.target.value, 'mobile_no')}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              required
              label='Personal Email Address'
              value={formvalue.email_address}
              error={errorHandler?.email_address && isSubmit && !formvalue.email_address}
              helperText={errorHandler?.email_address}
              onChange={(e) => onChangeFormvalueHandler(e.target.value, 'email_address')}
            />
          </Grid>
        </Grid>

        <Grid container spacing={2} sx={{ mt: '2rem' }}>
          <Grid item xs={12}>
            Employment Details
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth required>
              <InputLabel>Affiliate Description</InputLabel>
              <Select
                fullWidth
                label='Affiliate Description'
                value={formvalue.company}
                error={errorHandler?.company && isSubmit && !formvalue.company}
                onChange={(e) => onChangeFormvalueHandler(e.target.value, 'company')}
              >
                {companyList.length > 0 &&
                  companyList.map((e) => (
                    <MenuItem key={e} value={e}>
                      {e}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
            {errorHandler?.company && (
              <InputLabel sx={{ fontSize: '0.75rem', color: '#d32f2f', marginLeft: '.80rem' }}>
                {errorHandler.company}
              </InputLabel>
            )}
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              label='Employee Code'
              value={formvalue.code}
              error={errorHandler?.code && isSubmit && !formvalue.code}
              helperText={errorHandler?.code}
              onChange={(e) => onChangeFormvalueHandler(e.target.value, 'code')}
            />
          </Grid>
          {fetchInfo && (
            <Grid item xs={12} sx={{ mt: '1rem' }}>
              <LinearProgress sx={{ mb: '0.5rem' }} />
            </Grid>
          )}

          {employeeInfo.data.code && (
            <Box sx={styles.hideFormContainer}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <AutoFilledData
                    fullWidth
                    required
                    label='Position'
                    value={employeeInfo.data.position}
                    InputProps={{
                      readOnly: true,
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <AutoFilledData
                    fullWidth
                    required
                    label='Department'
                    value={employeeInfo.data.department}
                    InputProps={{
                      readOnly: true,
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <AutoFilledData
                    fullWidth
                    required
                    label='Base of Assignment'
                    value={employeeInfo.data.base_of_assignment}
                    InputProps={{
                      readOnly: true,
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <AutoFilledData
                    fullWidth
                    required
                    label='Date Hired'
                    value={employeeInfo.data.hiring_date}
                    InputProps={{
                      readOnly: true,
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <AutoFilledData
                    fullWidth
                    required
                    label='Clearance Application Date'
                    value={formvalue.clearance_application_date}
                    InputProps={{
                      readOnly: true,
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <DatePicker
                    views={['year', 'month', 'day']}
                    label='Last Working Day'
                    inputFormat={'YYYY-MM-DD'}
                    value={formvalue.last_working_day === '' ? null : formvalue.last_working_day}
                    onChange={(e) => {
                      const date = moment(e).format('YYYY-MM-DD');
                      onChangeFormvalueHandler(date, 'last_working_day');
                    }}
                    renderInput={(params) => (
                      <TextField
                        required
                        fullWidth
                        {...params}
                        error={
                          errorHandler?.last_working_day && isSubmit && !formvalue.last_working_day
                        }
                        helperText={errorHandler?.last_working_day}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Reason for Separation</InputLabel>
                    <Select
                      fullWidth
                      label='Reason for Separation'
                      value={formvalue.reason}
                      error={errorHandler?.reason && isSubmit && !formvalue.reason}
                      onChange={(e) => onChangeFormvalueHandler(e.target.value, 'reason')}
                    >
                      {reasonList.length > 0 &&
                        reasonList.map((e) => (
                          <MenuItem key={e} value={e}>
                            {e}
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                  {errorHandler?.reason && (
                    <InputLabel
                      sx={{ fontSize: '0.75rem', color: '#d32f2f', marginLeft: '.80rem' }}
                    >
                      {errorHandler.reason}
                    </InputLabel>
                  )}
                </Grid>

                {formvalue.reason === 'Dissatisfaction at Work' && (
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <InputLabel>Dissatisfaction Cause</InputLabel>
                      <Select
                        fullWidth
                        label='Dissatisfaction Cause'
                        value={formvalue.dissatisfaction_cause}
                        error={
                          errorHandler?.dissatisfaction_cause &&
                          isSubmit &&
                          !formvalue.dissatisfaction_cause
                        }
                        onChange={(e) =>
                          onChangeFormvalueHandler(e.target.value, 'dissatisfaction_cause')
                        }
                      >
                        {dissatisfactionCauseList.length > 0 &&
                          dissatisfactionCauseList.map((e) => (
                            <MenuItem key={e} value={e}>
                              {e}
                            </MenuItem>
                          ))}
                      </Select>
                    </FormControl>
                    {errorHandler?.dissatisfaction_cause && (
                      <InputLabel
                        sx={{ fontSize: '0.75rem', color: '#d32f2f', marginLeft: '.80rem' }}
                      >
                        {errorHandler.dissatisfaction_cause}
                      </InputLabel>
                    )}
                  </Grid>
                )}

                {formvalue.reason === 'End of Contract' && (
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <InputLabel>End of Contract</InputLabel>
                      <Select
                        fullWidth
                        label='End of Contract'
                        value={formvalue.end_contract_cause}
                        error={
                          errorHandler?.end_contract_cause &&
                          isSubmit &&
                          !formvalue.end_contract_cause
                        }
                        onChange={(e) =>
                          onChangeFormvalueHandler(e.target.value, 'end_contract_cause')
                        }
                      >
                        {endContractCauseList.length > 0 &&
                          endContractCauseList.map((e) => (
                            <MenuItem key={e} value={e}>
                              {e}
                            </MenuItem>
                          ))}
                      </Select>
                    </FormControl>
                    {errorHandler?.end_contract_cause && (
                      <InputLabel
                        sx={{ fontSize: '0.75rem', color: '#d32f2f', marginLeft: '.80rem' }}
                      >
                        {errorHandler.end_contract_cause}
                      </InputLabel>
                    )}
                  </Grid>
                )}

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label='Remarks'
                    multiline
                    rows={4}
                    value={formvalue.remarks}
                    error={errorHandler?.remarks && isSubmit && !formvalue.remarks}
                    helperText={errorHandler?.remarks}
                    onChange={(e) => onChangeFormvalueHandler(e.target.value, 'remarks')}
                  />
                </Grid>
              </Grid>
            </Box>
          )}
        </Grid>

        {module === 'hr' ? (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginTop: '2rem' }}>
            <Button
              sx={{ padding: '0.5rem 3rem', borderRadius: '2rem', mr: 1 }}
              variant='contained'
              onClick={() => {
                onSubmitFormHandler();
              }}
            >
              Proceed
            </Button>

            <Button
              sx={{ padding: '0.5rem 3rem', borderRadius: '2rem' }}
              variant='outlined'
              onClick={() => {
                navigate('/hr');
              }}
            >
              Cancel
            </Button>
          </Box>
        ) : (
          <Grid container spacing={2} sx={{ mt: '2rem' }}>
            <Grid item sm={6} xs={12}>
              <Button
                fullWidth
                sx={{ p: 2 }}
                variant='contained'
                size='large'
                disableElevation
                onClick={() => {
                  onSubmitFormHandler();
                }}
              >
                Proceed
              </Button>
            </Grid>

            <Grid item sm={6} xs={12}>
              <Button
                fullWidth
                sx={{ p: 2 }}
                variant='outlined'
                disableElevation
                onClick={() => {
                  navigate('/');
                }}
              >
                Cancel
              </Button>
            </Grid>
          </Grid>
        )}
      </Box>
    </Box>
  );
};

export default EmployeeRegistrationForm;
